import {
  AccessControlIcon,
  AddonsIcon,
  BillingContactsIcon,
  CalendarDaysIcon,
  CalendarXMarkIcon,
  ClapperboardPlayIcon,
  CodeIcon,
  CalendarCheckIcon as ConfirmationIcon,
  CreditCardIcon,
  ExitIcon,
  HourglassHalfIcon,
  InvoicesIcon,
  LangIcon,
  MailboxIcon as MailIcon,
  ModeratorsIcon,
  NotificationsIcon,
  PresentationIcon,
  RegistrationPageIcon,
  AlarmClockIcon as RemindersIcon,
  RewardsIcon,
  RobotIcon,
  SettingsIcon,
  ShareIcon,
  SubscriptionsIcon,
  SummaryIcon,
  TagIcon,
  ThemeIcon,
  TitleIcon,
  WelcomeMessagesIcon,
} from '$/images/FontAwesomeIcons';
import { useHeaderContext } from '$/sections/Home/HeaderContext';
import { useShowTeamRewards } from '$/sections/Home/Profile/PlanPage/BillingContacts';
import { useWindowSizeInfo } from '$/utils/useWindowSize';
import { css } from '@emotion/react';
import React from 'react';
import AccordionSideBar, { SectionTOCItem } from '../ui/AccordionSideBar';

export interface SideBarProps {
  title?: string;
  page?: string;
  excludeSections?: string[];
  className?: string;
  dynamicSectionsFn?: () => SectionTOCItem[];
  children?: React.ReactNode;
}

interface PageOptions {
  [key: string]: SectionTOCItem[];
}

const pageOptions: PageOptions = {
  schedule: [
    {
      id: 'SetSchedule',
      text: 'Event schedule',
      route: '#set',
      Icon: CalendarDaysIcon,
    },
    {
      id: 'JustInTime',
      text: 'Just-in-time sessions',
      route: '#jit',
      Icon: HourglassHalfIcon,
    },
    {
      id: 'ReplaySettings',
      text: 'Replays',
      route: '#replay',
      Icon: ClapperboardPlayIcon,
    },
    {
      id: 'BlackoutDates',
      text: 'Session availability',
      route: '#blackout',
      Icon: CalendarXMarkIcon,
    },
  ],
  notifications: [
    {
      id: 'Settings',
      text: 'Settings',
      route: '#settings',
      Icon: SettingsIcon,
    },
    {
      id: 'Confirmations',
      text: 'Confirmations',
      route: '#confirmations',
      Icon: ConfirmationIcon,
    },
    {
      id: 'Reminders',
      text: 'Reminders',
      route: '#reminders',
      Icon: RemindersIcon,
    },
    {
      id: 'FollowUps',
      text: 'Follow-ups',
      route: '#followups',
      Icon: MailIcon,
    },
  ],
  chat: [
    {
      id: 'Preferences',
      text: 'Preferences',
      route: '#preferences',
      Icon: SettingsIcon,
    },
    {
      id: 'Moderators',
      text: 'Moderators',
      route: '#moderators',
      Icon: ModeratorsIcon,
    },
    {
      id: 'Notifications',
      text: 'Notifications',
      route: '#notifications',
      Icon: NotificationsIcon,
    },
    {
      id: 'WelcomeMessages',
      text: 'Welcome messages',
      route: '#welcome',
      Icon: WelcomeMessagesIcon,
    },
    {
      id: 'AutoResponder',
      text: 'Auto response',
      route: '#responder',
      Icon: RobotIcon,
    },
  ],
  registration: [
    {
      id: 'RegistrationFlow',
      Icon: RegistrationPageIcon,
      route: '#pages',
      text: 'Registration pages',
    },
    {
      id: 'Theme',
      Icon: ThemeIcon,
      route: '#theme',
      text: 'Theme',
    },
    {
      id: 'RegNotifications',
      Icon: NotificationsIcon,
      route: '#notifications',
      text: 'Notifications',
    },
    {
      id: 'AccessControl',
      Icon: AccessControlIcon,
      route: '#access',
      text: 'Access control',
    },
    {
      id: 'RegTrackingScripts',
      Icon: CodeIcon,
      route: '#tracking-scripts',
      text: 'Scripts & pixels',
    },
  ],
  settings: [
    {
      id: 'Language',
      text: 'Language',
      route: '#lang',
      Icon: LangIcon,
    },
    {
      id: 'Title',
      text: 'Title',
      route: '#titles',
      Icon: TitleIcon,
    },
    {
      id: 'PresentationRoom',
      text: 'Webinar room',
      route: '#presentation',
      Icon: PresentationIcon,
    },
    {
      id: 'ExitRoom',
      text: 'Exit room',
      route: '#exit',
      Icon: ExitIcon,
    },
    {
      id: 'Sharing',
      text: 'Sharing',
      route: '#sharing',
      Icon: ShareIcon,
    },
    {
      id: 'Tags',
      text: 'Tags & integrations',
      route: '#tags',
      Icon: TagIcon,
    },
  ],
  myplan: [
    {
      id: 'm-summary',
      text: 'Summary',
      route: '#summary',
      Icon: SummaryIcon,
    },
    {
      id: 'm-subscriptions',
      text: 'Subscription details',
      route: '#subscriptions',
      Icon: SubscriptionsIcon,
    },
    {
      id: 'm-addons',
      text: 'Available add-ons',
      route: '#addons',
      Icon: AddonsIcon,
    },
    {
      id: 'm-card',
      text: 'Payment details',
      route: '#card',
      Icon: CreditCardIcon,
    },
    {
      id: 'm-contacts',
      text: 'Billing contacts',
      route: '#contacts',
      Icon: BillingContactsIcon,
    },
    {
      id: 'm-rewards',
      text: 'Team rewards',
      route: '#rewards',
      Icon: RewardsIcon,
      showFn: useShowTeamRewards,
    },
    {
      id: 'm-invoices',
      text: 'Invoices',
      route: '#invoices',
      Icon: InvoicesIcon,
    },
  ],
};

export const SideBar: React.FC<SideBarProps> = ({
  title,
  page,
  excludeSections,
  dynamicSectionsFn,
}) => {
  const { md: isMd } = useWindowSizeInfo();
  const { announcementHeight, headerHeight } = useHeaderContext();

  const sections = React.useMemo(() => {
    if (dynamicSectionsFn)
      return dynamicSectionsFn().map((p) => {
        // Make sure ID of menu item is not same as ID of static sections
        return { ...p, id: `m-${p.id}` };
      });

    if (!page) return [];

    return excludeSections
      ? pageOptions[page].filter((option) => !excludeSections.includes(option.id))
      : pageOptions[page];
  }, [dynamicSectionsFn, excludeSections, page]);

  const top = headerHeight + announcementHeight ?? 100;
  return (
    <div className={`${page ? 'w-20e' : 'flex w-full'} ${isMd ? '' : 'hidden'}`}>
      <div
        id='common-sections'
        className='sticky flex flex-col-reverse pr-1e'
        css={css`
          align-self: flex-start;
          top: calc(${top}px + 1rem);
        `}
      >
        {page && <AccordionSideBar title={title} sections={sections} />}
        <div id='belowSideBar' className='w-full flex flex-col flex-shrink pt-1e' />
      </div>
    </div>
  );
};

export default SideBar;
